/** Shared document + Open Graph meta (frontend + Cloudflare Pages middleware). */

export const APP_TITLE = '香港手球裁判分析系統';

export const DEFAULT_META_DESCRIPTION =
  '香港手球裁判分析系統 — 賽事影片分析與即時記錄';

/** Production canonical origin for absolute og:image / og:url. */
export const SITE_ORIGIN = 'https://data.hkhbref.com';

/** Same-origin PNG for WhatsApp / Facebook / Twitter previews (see icon generate script). */
export const SOCIAL_PREVIEW_IMAGE_PATH = '/icon-512.png';

export const SOCIAL_PREVIEW_IMAGE_WIDTH = 512;
export const SOCIAL_PREVIEW_IMAGE_HEIGHT = 512;

export function formatDocumentTitle(pageLabel: string): string {
  const t = pageLabel.trim();
  if (!t) return APP_TITLE;
  return `${t} | ${APP_TITLE}`;
}

/** Longest / most specific patterns first. */
const ROUTE_PAGE_LABELS: readonly { test: (pathname: string) => boolean; title: string }[] = [
  { test: (p) => /^\/review\/[^/]+$/.test(p), title: '影片分析詳情' },
  { test: (p) => /^\/live\/[^/]+$/.test(p), title: '即時記錄詳情' },
  { test: (p) => /^\/groups\/[^/]+$/.test(p), title: '分析小組詳情' },
  { test: (p) => p === '/sso', title: 'SSO 登入' },
  { test: (p) => p === '/login', title: '登入' },
  { test: (p) => p === '/dashboard', title: '數據總覽' },
  { test: (p) => p === '/review', title: '影片分析' },
  { test: (p) => /^\/scoreboard\/[^/]+\/display$/.test(p), title: '比分顯示' },
  { test: (p) => /^\/scoreboard\/[^/]+$/.test(p), title: '比分控制台' },
  { test: (p) => p === '/live', title: '即時記錄' },
  { test: (p) => p === '/groups', title: '分析小組' },
  { test: (p) => p === '/scoreboard', title: '賽事比分牌' },
];

export function pageLabelForPathname(pathname: string): string {
  const path = pathname || '/';
  for (const route of ROUTE_PAGE_LABELS) {
    if (route.test(path)) return route.title;
  }
  return '';
}

export function documentTitleForPathname(pathname: string): string {
  return formatDocumentTitle(pageLabelForPathname(pathname));
}

export function socialPreviewImageUrl(origin: string = SITE_ORIGIN): string {
  const base = origin.replace(/\/$/, '');
  return `${base}${SOCIAL_PREVIEW_IMAGE_PATH}`;
}

const UUID_RE = /^[a-f0-9-]{36}$/i;

/** Minimal D1 surface for social meta lookups (Worker + Pages). */
export interface SocialMetaDb {
  prepare(query: string): {
    bind(...values: unknown[]): {
      first<T>(): Promise<T | null>;
    };
  };
}

function liveMatchHeadline(homeTeam?: string | null, awayTeam?: string | null): string {
  const home = homeTeam?.trim() || '主隊';
  const away = awayTeam?.trim() || '客隊';
  return `${home} vs ${away}`;
}

export async function resolveSocialPageLabel(
  db: SocialMetaDb,
  pathname: string
): Promise<string | null> {
  const path = pathname || '/';

  const reviewMatch = path.match(/^\/review\/([^/]+)$/);
  if (reviewMatch && UUID_RE.test(reviewMatch[1])) {
    const row = await db
      .prepare(`SELECT title FROM review_sessions WHERE id = ?`)
      .bind(reviewMatch[1])
      .first<{ title: string }>();
    return row?.title?.trim() || null;
  }

  const liveMatch = path.match(/^\/live\/([^/]+)$/);
  if (liveMatch && UUID_RE.test(liveMatch[1])) {
    const row = await db
      .prepare(`SELECT home_team, away_team FROM live_sessions WHERE id = ?`)
      .bind(liveMatch[1])
      .first<{ home_team: string | null; away_team: string | null }>();
    if (!row) return null;
    return liveMatchHeadline(row.home_team, row.away_team);
  }

  const groupMatch = path.match(/^\/groups\/([^/]+)$/);
  if (groupMatch && UUID_RE.test(groupMatch[1])) {
    const row = await db
      .prepare(`SELECT name FROM review_share_groups WHERE id = ?`)
      .bind(groupMatch[1])
      .first<{ name: string }>();
    return row?.name?.trim() || null;
  }

  return null;
}

export interface SocialPreviewBindings {
  DB?: SocialMetaDb;
  API?: { fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> };
}

export async function fetchSocialPageLabelViaBinding(
  api: SocialPreviewBindings['API'],
  pathname: string
): Promise<string | null> {
  if (!api) return null;
  try {
    const req = new Request(
      `https://data.hkhbref.com/api/public/social-meta?path=${encodeURIComponent(pathname)}`,
      { headers: { Accept: 'application/json' } }
    );
    const res = await api.fetch(req);
    if (!res.ok) return null;
    const data = (await res.json()) as { pageLabel?: string | null };
    return data.pageLabel?.trim() || null;
  } catch {
    return null;
  }
}

async function fetchSocialPageLabelFromMetaRoute(
  origin: string,
  pathname: string
): Promise<string | null> {
  try {
    const url = new URL('/meta/social', origin);
    url.searchParams.set('path', pathname);
    const res = await fetch(url.toString(), {
      headers: { Accept: 'application/json', 'User-Agent': 'hkhbref-data-social-meta/1' },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { pageLabel?: string | null };
    return data.pageLabel?.trim() || null;
  } catch {
    return null;
  }
}

/** Resolve entity title for link-preview crawlers. */
export async function resolveSocialPageLabelForPreview(
  env: SocialPreviewBindings,
  origin: string,
  pathname: string
): Promise<string | null> {
  if (env.DB) {
    return resolveSocialPageLabel(env.DB, pathname);
  }
  if (env.API) {
    const viaBinding = await fetchSocialPageLabelViaBinding(env.API, pathname);
    if (viaBinding) return viaBinding;
  }
  const viaMetaRoute = await fetchSocialPageLabelFromMetaRoute(origin, pathname);
  if (viaMetaRoute) return viaMetaRoute;
  return fetchSocialPageLabelFromPublicApi(pathname);
}

async function fetchSocialPageLabelFromPublicApi(pathname: string): Promise<string | null> {
  try {
    const url = new URL('/api/public/social-meta', SITE_ORIGIN);
    url.searchParams.set('path', pathname);
    const res = await fetch(url.toString(), {
      headers: { Accept: 'application/json', 'User-Agent': 'hkhbref-data-social-meta/1' },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { pageLabel?: string | null };
    return data.pageLabel?.trim() || null;
  } catch {
    return null;
  }
}

export async function fetchSocialPageLabel(origin: string, pathname: string): Promise<string | null> {
  return resolveSocialPageLabelForPreview({}, origin, pathname);
}

export interface SocialDocumentMeta {
  title: string;
  description: string;
  url: string;
  image: string;
  siteName: string;
  imageWidth: number;
  imageHeight: number;
}

export function buildSocialDocumentMeta(
  pathname: string,
  origin: string = SITE_ORIGIN,
  search = '',
  pageLabelOverride?: string | null
): SocialDocumentMeta {
  const base = origin.replace(/\/$/, '');
  const path = pathname || '/';
  const label = pageLabelOverride?.trim() || pageLabelForPathname(path);
  return {
    title: formatDocumentTitle(label),
    description: DEFAULT_META_DESCRIPTION,
    url: `${base}${path}${search}`,
    image: socialPreviewImageUrl(base),
    siteName: APP_TITLE,
    imageWidth: SOCIAL_PREVIEW_IMAGE_WIDTH,
    imageHeight: SOCIAL_PREVIEW_IMAGE_HEIGHT,
  };
}

const BOT_UA =
  /facebookexternalhit|WhatsApp|Twitterbot|LinkedInBot|Slackbot|TelegramBot|Discordbot|Pinterest|Googlebot/i;

export function isSocialPreviewBot(userAgent: string | null | undefined): boolean {
  return BOT_UA.test(userAgent ?? '');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function replaceMetaContent(html: string, selector: string, content: string): string {
  const escaped = escapeHtml(content);
  const re = new RegExp(
    `(<meta[^>]+${selector}[^>]*content=")[^"]*("[^>]*>)`,
    'i'
  );
  if (re.test(html)) return html.replace(re, `$1${escaped}$2`);
  return html;
}

/** Inject / update social meta in static index.html for link-preview crawlers. */
export function injectSocialMetaIntoHtml(html: string, meta: SocialDocumentMeta): string {
  let out = html.replace(
    /<title>[^<]*<\/title>/i,
    `<title>${escapeHtml(meta.title)}</title>`
  );

  out = replaceMetaContent(out, 'name="description"', meta.description);
  out = replaceMetaContent(out, 'property="og:type"', 'website');
  out = replaceMetaContent(out, 'property="og:site_name"', meta.siteName);
  out = replaceMetaContent(out, 'property="og:title"', meta.title);
  out = replaceMetaContent(out, 'property="og:description"', meta.description);
  out = replaceMetaContent(out, 'property="og:url"', meta.url);
  out = replaceMetaContent(out, 'property="og:image"', meta.image);
  out = replaceMetaContent(out, 'property="og:image:secure_url"', meta.image);
  out = replaceMetaContent(out, 'property="og:image:type"', 'image/png');
  out = replaceMetaContent(out, 'property="og:image:width"', String(meta.imageWidth));
  out = replaceMetaContent(out, 'property="og:image:height"', String(meta.imageHeight));
  out = replaceMetaContent(out, 'name="twitter:card"', 'summary_large_image');
  out = replaceMetaContent(out, 'name="twitter:title"', meta.title);
  out = replaceMetaContent(out, 'name="twitter:description"', meta.description);
  out = replaceMetaContent(out, 'name="twitter:image"', meta.image);

  return out;
}
